import {
  ArrowRight,
  BarChart3,
  BookOpen,
  Brain,
  Check,
  ChevronRight,
  Clipboard,
  Copy,
  Crown,
  FileText,
  Flame,
  LayoutDashboard,
  Lightbulb,
  Loader2,
  Menu,
  PackageSearch,
  PenLine,
  Search,
  Settings,
  Sparkles,
  Store,
  Tag,
  Target,
  TextCursorInput,
  Wand2,
  X
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { generateContent, getHealth, type GenerateSuccess } from "./lib/api";
import {
  RESEARCH_TOOLS,
  TOOLS,
  defaultTool,
  findResearchTool,
  toolFromLocation,
  type OutputKey,
  type ResearchTool,
  type ToolDefinition,
  type ToolField,
  type ToolId
} from "./lib/tools";
import {
  addHistoryItem,
  loadHistory,
  loadOnboardingData,
  loadShopInput,
  makeHistoryId,
  onboardingComplete,
  saveOnboarding,
  saveShopInput,
  type GenerationHistoryItem
} from "./lib/storage";

interface RouteState {
  pathname: string;
  search: string;
}

type OnboardingAnswers = Record<string, string | boolean>;

const toolIcons: Record<ToolId, LucideIcon> = {
  description: FileText,
  title: TextCursorInput,
  tags: Tag,
  keywords: Search,
  ideas: Lightbulb,
  seo: Target,
  "listing-studio": Wand2
};

const researchIcons: Record<string, LucideIcon> = {
  "best-sellers": Crown,
  "etsy-trends": Flame,
  "niche-finder": Brain,
  "shop-analyzer": Store,
  "listing-analyzer": PackageSearch
};

const onboardingSteps = [
  {
    key: "primary_goal",
    prompt: "What is your primary goal with EtsyGenerator?",
    options: [
      ["optimize_listings", "Optimize listings"],
      ["find_keywords", "Find keywords"],
      ["write_faster", "Write faster"],
      ["find_product_ideas", "Find product ideas"],
      ["open_shop", "Open a shop"],
      ["not_sure", "Not sure yet"]
    ]
  },
  {
    key: "business_type",
    prompt: "Which best describes your shop?",
    options: [
      ["side_hustle", "Side hustle"],
      ["full_time", "Full-time shop"],
      ["agency", "Agency or VA"],
      ["digital", "Digital seller"],
      ["just_starting", "Just starting"],
      ["other", "Other"]
    ]
  },
  {
    key: "product_type",
    prompt: "What products do you sell or plan to sell?",
    options: [
      ["handmade", "Handmade"],
      ["craft_supplies", "Craft supplies"],
      ["vintage", "Vintage"],
      ["digital", "Digital"],
      ["print_on_demand", "Print-on-demand"],
      ["mix", "A mix"]
    ]
  },
  {
    key: "referral_source",
    prompt: "How did you hear about EtsyGenerator?",
    options: [
      ["google", "Google"],
      ["youtube", "YouTube"],
      ["etsy", "Etsy"],
      ["reddit", "Reddit"],
      ["friend", "Friend"],
      ["other", "Other"]
    ]
  }
];

function classNames(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

function useRoute(): [RouteState, (href: string) => void] {
  const [route, setRoute] = useState<RouteState>(() => ({
    pathname: window.location.pathname,
    search: window.location.search
  }));

  useEffect(() => {
    const onPopState = () => {
      setRoute({
        pathname: window.location.pathname,
        search: window.location.search
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = (href: string) => {
    const url = new URL(href, window.location.origin);
    window.history.pushState(null, "", `${url.pathname}${url.search}`);
    setRoute({
      pathname: url.pathname,
      search: url.search
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return [route, navigate];
}

interface LinkProps {
  href: string;
  className?: string;
  children: ReactNode;
  navigate: (href: string) => void;
  onClick?: () => void;
}

function Link({ href, className, children, navigate, onClick }: LinkProps) {
  return (
    <a
      href={href}
      className={className}
      onClick={(event) => {
        event.preventDefault();
        onClick?.();
        navigate(href);
      }}
    >
      {children}
    </a>
  );
}

function App() {
  const [route, navigate] = useRoute();
  const activeTool = toolFromLocation(route.pathname, route.search);
  const researchTool = findResearchTool(route.pathname);

  if (route.pathname === "/" || route.pathname === "/plan" || route.pathname === "/contact" || route.pathname === "/login") {
    return <PublicPage route={route} navigate={navigate} />;
  }

  if (route.pathname === "/dashboard" || route.pathname === "/account/dashboard") {
    return (
      <AppShell route={route} navigate={navigate}>
        <Dashboard navigate={navigate} />
      </AppShell>
    );
  }

  if (activeTool) {
    return (
      <AppShell route={route} navigate={navigate}>
        <ToolPage tool={activeTool} />
      </AppShell>
    );
  }

  if (researchTool) {
    return (
      <AppShell route={route} navigate={navigate}>
        <ResearchUnavailable tool={researchTool} navigate={navigate} />
      </AppShell>
    );
  }

  return (
    <PublicLayout route={route} navigate={navigate}>
      <section className="mx-auto max-w-4xl px-6 py-24 text-center">
        <p className="text-sm font-semibold uppercase tracking-[0.18em] text-coral">404</p>
        <h1 className="mt-4 text-4xl font-black text-ink">This page is not part of the restored free build.</h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-ink/70">
          The main dashboard and generator tools are available now.
        </p>
        <Link
          href="/account/dashboard"
          navigate={navigate}
          className="mt-8 inline-flex items-center gap-2 rounded-md bg-moss px-5 py-3 font-semibold text-white shadow-soft hover:bg-ink"
        >
          Open dashboard <ArrowRight className="h-4 w-4" />
        </Link>
      </section>
    </PublicLayout>
  );
}

function PublicPage({ route, navigate }: { route: RouteState; navigate: (href: string) => void }) {
  if (route.pathname === "/plan") {
    return (
      <PublicLayout route={route} navigate={navigate}>
        <PlanPage navigate={navigate} />
      </PublicLayout>
    );
  }

  if (route.pathname === "/contact") {
    return (
      <PublicLayout route={route} navigate={navigate}>
        <ContactPage navigate={navigate} />
      </PublicLayout>
    );
  }

  if (route.pathname === "/login") {
    return (
      <PublicLayout route={route} navigate={navigate}>
        <LoginPage navigate={navigate} />
      </PublicLayout>
    );
  }

  return (
    <PublicLayout route={route} navigate={navigate}>
      <LandingPage navigate={navigate} />
    </PublicLayout>
  );
}

function PublicLayout({
  route,
  navigate,
  children
}: {
  route: RouteState;
  navigate: (href: string) => void;
  children: ReactNode;
}) {
  const navItems: Array<[string, string]> = [
    ["Etsys top sellers", "/tools/best-sellers"],
    ["Etsy digital downloads", "/tools/product-idea-generator"],
    ["Pricing", "/plan"],
    ["Contact", "/contact"],
    ["Login", "/login"]
  ];

  return (
    <div className="min-h-screen bg-paper text-ink">
      <header className="sticky top-0 z-40 border-b border-moss/10 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-[78px] max-w-7xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
          <Link href="/" navigate={navigate} className="flex min-w-0 items-center">
            <img src="/assets/etsygenerator-logo.png" alt="EtsyGenerator" className="h-10 w-auto sm:h-11" />
          </Link>
          <nav className="hidden items-center gap-7 lg:flex">
            {navItems.map(([label, href]) => (
              <Link
                key={href}
                href={href}
                navigate={navigate}
                className={classNames(
                  "whitespace-nowrap text-[15px] font-bold transition hover:text-coral",
                  route.pathname === href ? "text-coral" : "text-ink/70"
                )}
              >
                {label}
              </Link>
            ))}
          </nav>
          <Link
            href="/account/dashboard"
            navigate={navigate}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-moss px-3 py-2.5 text-sm font-bold text-white shadow-soft hover:bg-ink sm:px-5"
          >
            <span className="hidden sm:inline">Get started</span>
            <span className="sm:hidden">Start</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </header>
      {children}
    </div>
  );
}

function LandingPage({ navigate }: { navigate: (href: string) => void }) {
  const featuredTools = TOOLS.slice(0, 7);
  const heroBenefits = [
    "Launch etsy products faster",
    "Boost etsy shop SEO",
    "Optimize listing creation with AI"
  ];
  const valueProps = [
    [
      "Grow your etsy shop with AI generator",
      "Create listing copy from your product details, buyer intent, and tone without staring at a blank page."
    ],
    [
      "Effortless etsy listing creation",
      "Move from product notes to usable Etsy titles, descriptions, tags, and listing sections in one workspace."
    ],
    [
      "Generate tags, titles, SEO descriptions",
      "Use focused tools for descriptions, titles, tags, keywords, product ideas, SEO plans, and complete listing drafts."
    ]
  ];
  const steps = [
    [
      "Step 1",
      "Choose Etsy AI tool",
      "Pick a generator for your current task: product descriptions, Etsy tags, product titles, keywords, ideas, SEO planning, or the listing studio.",
      "/assets/choose.png"
    ],
    [
      "Step 2",
      "Enter etsy product details",
      "Add your product, materials, audience, style, keywords, and shop notes so the output matches the listing you want to publish.",
      "/assets/result.png"
    ],
    [
      "Step 3",
      "Click Generate button",
      "Generate a first draft, copy the result, then refine or regenerate while your recent work stays in local browser history.",
      "/assets/tag.png"
    ]
  ];

  return (
    <>
      <section className="overflow-hidden bg-[linear-gradient(180deg,#f7fbf4_0%,#fbfaf6_58%,#ffffff_100%)]">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-14 sm:px-6 sm:pb-20 sm:pt-20 lg:px-8">
          <div className="mx-auto max-w-5xl text-center">
            <p className="text-sm font-black uppercase tracking-[0.22em] text-coral">Etsy SEO generator</p>
            <h1 className="mx-auto mt-5 max-w-4xl text-4xl font-black leading-[1.05] text-ink sm:text-6xl lg:text-7xl">
              10x your etsy shop SALES
              <span className="mt-2 block bg-[linear-gradient(90deg,#df6043,#47a777,#23483c)] bg-clip-text text-transparent">
                title generator<span className="typed-cursor text-coral">|</span>
              </span>
            </h1>
            <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-ink/70 sm:text-xl">
              The AI superpower for Etsy sellers: write etsy descriptions, create SEO titles, generate tags, and build better listing drafts for free.
            </p>
            <div className="mt-8 flex justify-center">
              <Link
                href="/document-create?type=19"
                navigate={navigate}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-coral px-5 py-3 font-bold text-white shadow-soft hover:bg-[#bf563b]"
              >
                Generate for Free <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="mx-auto mt-12 max-w-6xl rounded-lg border border-mint/35 bg-white p-2 shadow-soft">
            <img
              src="/assets/home.jpg"
              alt="EtsyGenerator editor preview"
              className="h-auto w-full rounded-md object-cover"
              loading="eager"
            />
          </div>

          <div className="mx-auto mt-8 grid max-w-5xl gap-4 sm:grid-cols-3">
            {heroBenefits.map((item) => (
              <div key={item} className="flex items-center justify-center gap-3 text-sm font-bold text-ink/74 sm:justify-start">
                <Check className="h-5 w-5 flex-none text-mint" />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center justify-center gap-4 sm:flex-row">
            <img src="/assets/trustpilot_reviews_2.svg" alt="Trustpilot reviews" className="h-10 w-auto max-w-full" loading="eager" />
            <img src="/assets/capterra_reviews_2.svg" alt="Capterra reviews" className="h-10 w-auto max-w-full" loading="eager" />
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 py-16 text-center lg:px-8">
          <h2 className="mx-auto max-w-4xl text-3xl font-black leading-tight text-ink sm:text-5xl">
            Save time when launching your etsy products. Use our AI powered etsy product description generator.
          </h2>
          <div className="mt-12 grid gap-8 text-left md:grid-cols-3">
            {valueProps.map(([title, text]) => (
              <div key={title} className="rounded-lg border border-moss/10 bg-paper p-6">
                <span className="flex h-11 w-11 items-center justify-center rounded-md bg-mint/12 text-moss">
                  <Sparkles className="h-5 w-5" />
                </span>
                <h3 className="mt-5 text-xl font-black text-ink">{title}</h3>
                <p className="mt-3 leading-7 text-ink/68">{text}</p>
              </div>
            ))}
          </div>
          <Link
            href="/tools/description-generator"
            navigate={navigate}
            className="mt-10 inline-flex items-center justify-center gap-2 rounded-md bg-moss px-5 py-3 font-bold text-white shadow-soft hover:bg-ink"
          >
            Try a demo <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <section className="bg-cloud">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
          <div className="grid gap-12">
            {steps.map(([eyebrow, title, text, src], index) => (
              <div
                key={title}
                className={classNames(
                  "grid gap-8 lg:grid-cols-2 lg:items-center",
                  index % 2 === 1 && "lg:[&>div:first-child]:order-2"
                )}
              >
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.2em] text-coral">{eyebrow}</p>
                  <h2 className="mt-4 text-3xl font-black text-ink sm:text-5xl">{title}</h2>
                  <p className="mt-5 text-lg leading-8 text-ink/70">{text}</p>
                  <Link
                    href={index === 0 ? "/account/dashboard" : "/tools/description-generator"}
                    navigate={navigate}
                    className="mt-7 inline-flex items-center gap-2 rounded-md bg-coral px-5 py-3 font-bold text-white hover:bg-[#bf563b]"
                  >
                    {index === 0 ? "Choose a tool" : "Generate now"} <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
                <div className="rounded-lg border border-moss/10 bg-white p-2 shadow-soft">
                  <img src={src} alt="" className="h-auto w-full rounded-md object-cover" loading="eager" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-coral">Core tools</p>
            <h2 className="mt-4 text-4xl font-black text-ink sm:text-5xl">Supercharge your etsy shop with Ai.</h2>
            <p className="mt-5 text-lg leading-8 text-ink/70">
              Start with the restored free writing tools. Market research tools stay visible with clear unavailable states until real Etsy data is connected.
            </p>
            <div className="mt-7 grid gap-3">
              {[
                ["OpenRouter free models only", "No paid model fallback is configured in this restoration."],
                ["No account required", "Old login and subscription gates are bypassed for the free build."],
                ["Local history", "Generated drafts stay in this browser unless you copy them elsewhere."]
              ].map(([title, text]) => (
                <div key={title} className="flex gap-3">
                  <Check className="mt-1 h-5 w-5 flex-none text-mint" />
                  <div>
                    <h3 className="font-bold text-ink">{title}</h3>
                    <p className="text-sm leading-6 text-ink/68">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {featuredTools.map((tool) => {
              const Icon = toolIcons[tool.id];
              return (
                <Link
                  key={tool.id}
                  href={`/tools/${tool.slug}`}
                  navigate={navigate}
                  className="group rounded-lg border border-moss/10 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:border-mint/50 hover:shadow-soft"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-md bg-mint/12 text-moss">
                      <Icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-bold text-ink">{tool.title}</h3>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink/46">{tool.eyebrow}</p>
                    </div>
                  </div>
                  <p className="mt-4 text-sm leading-6 text-ink/68">{tool.description}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-coral">
                    Open tool <ChevronRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:px-8">
          <div className="rounded-lg border border-moss/10 bg-paper p-2 shadow-soft">
            <img src="/assets/featured3.png" alt="EtsyGenerator feature preview" className="h-auto w-full rounded-md object-cover" loading="eager" />
          </div>
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-coral">Reviews</p>
            <h2 className="mt-4 text-4xl font-black text-ink sm:text-5xl">etsyGenerator received 4.8/5 stars in over 30+ reviews.</h2>
            <p className="mt-5 text-lg leading-8 text-ink/70">
              The public site keeps the familiar EtsyGenerator promise while the restored app removes broken paid gates and stale tracking scripts.
            </p>
            <div className="mt-7 flex flex-col gap-3 sm:flex-row">
              <img src="/assets/trustpilot_reviews_2.svg" alt="Trustpilot reviews" className="h-10 w-fit" loading="eager" />
              <img src="/assets/capterra_reviews_2.svg" alt="Capterra reviews" className="h-10 w-fit" loading="eager" />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8" id="pricing">
        <div className="rounded-lg bg-[linear-gradient(135deg,#21483c,#4aa987)] p-8 text-white shadow-soft sm:p-10 lg:grid lg:grid-cols-[1fr_auto] lg:items-center lg:gap-10">
          <div>
            <p className="text-sm font-black uppercase tracking-[0.2em] text-white/66">Pricing</p>
            <h2 className="mt-4 text-3xl font-black sm:text-5xl">Using etsyGenerator can save you hours of manual etsy listing creation.</h2>
            <p className="mt-5 max-w-3xl text-lg leading-8 text-white/78">
              This restored build is free, requires no login, and uses only OpenRouter free-model routing from the server.
            </p>
          </div>
          <div className="mt-8 rounded-lg bg-white p-6 text-ink lg:mt-0 lg:w-80">
            <div className="flex items-end justify-between gap-4">
              <div>
                <h3 className="text-2xl font-black">Free</h3>
                <p className="mt-1 text-sm text-ink/60">Restoration plan</p>
              </div>
              <div className="text-5xl font-black text-moss">$0</div>
            </div>
            <div className="mt-6 grid gap-3">
              {["No login required", "Description generator", "Title and tag tools", "Keyword ideas", "SEO planner", "Local history"].map((item) => (
                <div key={item} className="flex items-center gap-3 text-sm font-semibold text-ink/78">
                  <Check className="h-4 w-4 text-mint" />
                  {item}
                </div>
              ))}
            </div>
            <Link
              href="/account/dashboard"
              navigate={navigate}
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md bg-coral px-5 py-3 font-bold text-white hover:bg-[#bf563b]"
            >
              Get Started Free <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <FaqSection />

      <section className="bg-moss text-white">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 py-14 lg:grid-cols-[1fr_auto] lg:items-center lg:px-8">
          <div>
            <h2 className="text-3xl font-black">Generate your first restored Etsy draft.</h2>
            <p className="mt-3 max-w-2xl text-white/74">
              The old broken app route now resolves to the free description generator inside this build.
            </p>
          </div>
          <Link
            href="/document-create?type=19"
            navigate={navigate}
            className="inline-flex items-center justify-center gap-2 rounded-md bg-coral px-5 py-3 font-bold text-white hover:bg-[#bf563b]"
          >
            Open legacy route <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      <Footer navigate={navigate} />
    </>
  );
}

function FaqSection() {
  const faqs = [
    [
      "Is EtsyGenerator free now?",
      "Yes. This restoration has no paywall or forced login. Free OpenRouter model capacity can still be rate limited by the upstream provider."
    ],
    [
      "Does it use live Etsy marketplace data?",
      "No. The generator writes listing drafts from the details you provide. It does not claim live search volume, rank, sales, or trend data."
    ],
    [
      "What happened to the old app subscription system?",
      "It is not connected in this version. Old billing and login links were removed so the useful tools can work independently."
    ],
    [
      "Where is generation history stored?",
      "Recent generations are stored in browser localStorage for this first version. No D1, KV, R2, or user database is used."
    ]
  ];

  return (
    <section className="border-t border-moss/10 bg-cloud">
      <div className="mx-auto max-w-4xl px-6 py-16 lg:px-8">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-coral">FAQ</p>
        <h2 className="mt-4 text-4xl font-black text-ink">Questions about the restored build.</h2>
        <div className="mt-8 divide-y divide-moss/10 rounded-lg border border-moss/10 bg-white">
          {faqs.map(([question, answer]) => (
            <details key={question} className="group p-6">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-5 font-bold text-ink">
                {question}
                <ChevronRight className="h-5 w-5 flex-none transition group-open:rotate-90" />
              </summary>
              <p className="mt-4 leading-7 text-ink/68">{answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function PlanPage({ navigate }: { navigate: (href: string) => void }) {
  return (
    <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
      <div className="max-w-3xl">
        <p className="text-sm font-bold uppercase tracking-[0.2em] text-coral">Plan</p>
        <h1 className="mt-4 text-5xl font-black text-ink">Free Etsy AI tools, no subscription gate.</h1>
        <p className="mt-5 text-lg leading-8 text-ink/70">
          The restored version runs as a free Cloudflare Pages app. It needs an OpenRouter API key on the server, but users are not asked to pay or log in.
        </p>
      </div>
      <div className="mt-10 rounded-lg border border-moss/10 bg-white p-8 shadow-soft">
        <div className="grid gap-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <h2 className="text-3xl font-black">Free restoration plan</h2>
            <p className="mt-3 text-ink/68">
              Includes dashboard, onboarding, description, title, tag, keyword, product idea, SEO planner, and listing studio tools.
            </p>
          </div>
          <div className="text-6xl font-black text-moss">$0</div>
        </div>
        <Link
          href="/account/dashboard"
          navigate={navigate}
          className="mt-8 inline-flex items-center gap-2 rounded-md bg-moss px-5 py-3 font-bold text-white hover:bg-ink"
        >
          Open free workspace <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
}

function ContactPage({ navigate }: { navigate: (href: string) => void }) {
  return (
    <section className="mx-auto max-w-5xl px-6 py-16 lg:px-8">
      <p className="text-sm font-bold uppercase tracking-[0.2em] text-coral">Contact</p>
      <h1 className="mt-4 text-5xl font-black text-ink">EtsyGenerator support is in restoration mode.</h1>
      <p className="mt-5 max-w-3xl text-lg leading-8 text-ink/70">
        This page replaces the broken old contact routes with a clear status page. Production support channels can be wired back after the Cloudflare deploy target and account policies are confirmed.
      </p>
      <div className="mt-10 grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-moss/10 bg-white p-6">
          <h2 className="text-xl font-black">Current scope</h2>
          <p className="mt-3 leading-7 text-ink/68">Free generator tools, local history, and public pages are available.</p>
        </div>
        <div className="rounded-lg border border-moss/10 bg-white p-6">
          <h2 className="text-xl font-black">Deferred scope</h2>
          <p className="mt-3 leading-7 text-ink/68">Billing, accounts, email support, and live Etsy data are intentionally not connected in this version.</p>
        </div>
      </div>
      <Link
        href="/tools/description-generator"
        navigate={navigate}
        className="mt-8 inline-flex items-center gap-2 rounded-md bg-moss px-5 py-3 font-bold text-white hover:bg-ink"
      >
        Try the generator <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}

function LoginPage({ navigate }: { navigate: (href: string) => void }) {
  return (
    <section className="mx-auto max-w-4xl px-6 py-16 text-center lg:px-8">
      <p className="text-sm font-bold uppercase tracking-[0.2em] text-coral">Login bypassed</p>
      <h1 className="mt-4 text-5xl font-black text-ink">No login is required for the free build.</h1>
      <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-ink/70">
        The old login route is kept so old CTAs do not dead-end. It now routes into the restored free dashboard.
      </p>
      <Link
        href="/account/dashboard"
        navigate={navigate}
        className="mt-8 inline-flex items-center gap-2 rounded-md bg-moss px-5 py-3 font-bold text-white hover:bg-ink"
      >
        Continue to dashboard <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}

function Footer({ navigate }: { navigate: (href: string) => void }) {
  return (
    <footer className="bg-ink text-white">
      <div className="mx-auto grid max-w-7xl gap-8 px-6 py-10 md:grid-cols-[1fr_auto] md:items-center lg:px-8">
        <div>
          <img src="/assets/etsygenerator-logo.png" alt="EtsyGenerator" className="h-9 w-auto brightness-0 invert" />
          <p className="mt-4 max-w-xl text-sm leading-6 text-white/62">
            Free Etsy AI listing tools rebuilt for Cloudflare Pages. No old tracking scripts, no paid model fallback, no forced login.
          </p>
        </div>
        <div className="flex flex-wrap gap-4 text-sm font-semibold text-white/70">
          <Link href="/" navigate={navigate} className="hover:text-white">
            Home
          </Link>
          <Link href="/plan" navigate={navigate} className="hover:text-white">
            Plan
          </Link>
          <Link href="/account/dashboard" navigate={navigate} className="hover:text-white">
            Dashboard
          </Link>
          <Link href="/document-create?type=19" navigate={navigate} className="hover:text-white">
            Legacy route
          </Link>
        </div>
      </div>
    </footer>
  );
}

function AppShell({
  route,
  navigate,
  children
}: {
  route: RouteState;
  navigate: (href: string) => void;
  children: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen bg-cloud text-ink">
      <header className="fixed inset-x-0 top-0 z-40 h-16 border-b border-white/10 bg-moss text-white shadow-sm">
        <div className="flex h-full items-center gap-4 px-4 lg:px-6">
          <button
            type="button"
            className="rounded-md p-2 hover:bg-white/10 lg:hidden"
            aria-label="Open sidebar"
            onClick={() => setMobileOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
          <Link href="/" navigate={navigate} className="flex items-center gap-3">
            <img src="/assets/etsygenerator-logo.png" alt="EtsyGenerator" className="h-9 w-auto brightness-0 invert" />
          </Link>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden rounded-full bg-white/12 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-white/80 sm:inline-flex">
              Free workspace
            </span>
            <Link
              href="/tools/description-generator"
              navigate={navigate}
              className="inline-flex items-center gap-2 rounded-md bg-coral px-3 py-2 text-sm font-bold text-white hover:bg-[#bf563b]"
            >
              <Sparkles className="h-4 w-4" />
              Generate
            </Link>
          </div>
        </div>
      </header>

      <aside className="fixed bottom-0 left-0 top-16 z-30 hidden w-72 border-r border-moss/10 bg-white lg:block">
        <SidebarContent route={route} navigate={navigate} />
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            className="absolute inset-0 bg-ink/60"
            aria-label="Close sidebar"
            onClick={() => setMobileOpen(false)}
          />
          <div className="relative h-full w-80 max-w-[85vw] bg-white shadow-soft">
            <div className="flex h-16 items-center justify-between border-b border-moss/10 px-4">
              <span className="font-black">Tools</span>
              <button type="button" className="rounded-md p-2 hover:bg-cloud" onClick={() => setMobileOpen(false)} aria-label="Close sidebar">
                <X className="h-5 w-5" />
              </button>
            </div>
            <SidebarContent route={route} navigate={navigate} onNavigate={() => setMobileOpen(false)} />
          </div>
        </div>
      ) : null}

      <main className="pt-16 lg:pl-72">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-10 lg:py-8">{children}</div>
      </main>
    </div>
  );
}

function SidebarContent({
  route,
  navigate,
  onNavigate
}: {
  route: RouteState;
  navigate: (href: string) => void;
  onNavigate?: () => void;
}) {
  const researchBySection = useMemo(
    () => ({
      Brainstorm: RESEARCH_TOOLS.filter((tool) => tool.section === "Brainstorm"),
      Analyze: RESEARCH_TOOLS.filter((tool) => tool.section === "Analyze")
    }),
    []
  );

  return (
    <nav className="scrollbar-soft flex h-full flex-col overflow-y-auto px-4 py-5">
      <SidebarLink
        href="/account/dashboard"
        label="Dashboard"
        icon={LayoutDashboard}
        active={route.pathname === "/account/dashboard" || route.pathname === "/dashboard"}
        navigate={navigate}
        onNavigate={onNavigate}
      />

      <SidebarSection title="Create">
        {TOOLS.map((tool) => (
          <SidebarLink
            key={tool.id}
            href={`/tools/${tool.slug}`}
            label={tool.title}
            icon={toolIcons[tool.id]}
            active={route.pathname === `/tools/${tool.slug}` || (route.pathname === "/document-create" && tool.id === "description")}
            navigate={navigate}
            onNavigate={onNavigate}
          />
        ))}
      </SidebarSection>

      <SidebarSection title="Brainstorm">
        {researchBySection.Brainstorm.map((tool) => (
          <SidebarLink
            key={tool.slug}
            href={`/tools/${tool.slug}`}
            label={tool.title}
            icon={researchIcons[tool.slug] ?? BookOpen}
            active={route.pathname === `/tools/${tool.slug}`}
            navigate={navigate}
            onNavigate={onNavigate}
            muted
          />
        ))}
      </SidebarSection>

      <SidebarSection title="Analyze">
        {researchBySection.Analyze.map((tool) => (
          <SidebarLink
            key={tool.slug}
            href={`/tools/${tool.slug}`}
            label={tool.title}
            icon={researchIcons[tool.slug] ?? BarChart3}
            active={route.pathname === `/tools/${tool.slug}`}
            navigate={navigate}
            onNavigate={onNavigate}
            muted
          />
        ))}
      </SidebarSection>

      <div className="mt-auto rounded-lg border border-moss/10 bg-cloud p-4">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-ink/50">Runtime</p>
        <p className="mt-2 text-sm leading-6 text-ink/68">
          OpenRouter free models only. Live Etsy data tools are marked unavailable until connected.
        </p>
      </div>
    </nav>
  );
}

function SidebarSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-6">
      <p className="px-2 text-xs font-black uppercase tracking-[0.18em] text-ink/42">{title}</p>
      <div className="mt-2 space-y-1">{children}</div>
    </div>
  );
}

function SidebarLink({
  href,
  label,
  icon: Icon,
  active,
  navigate,
  onNavigate,
  muted
}: {
  href: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  navigate: (href: string) => void;
  onNavigate?: () => void;
  muted?: boolean;
}) {
  return (
    <Link
      href={href}
      navigate={navigate}
      onClick={onNavigate}
      className={classNames(
        "group flex items-center gap-3 rounded-md px-2 py-2 text-sm font-bold transition",
        active ? "bg-moss text-white" : "text-ink/72 hover:bg-cloud hover:text-moss"
      )}
    >
      <Icon className={classNames("h-5 w-5 flex-none", active ? "text-white" : muted ? "text-gold" : "text-mint")} />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function Dashboard({ navigate }: { navigate: (href: string) => void }) {
  const [history, setHistory] = useState<GenerationHistoryItem[]>(() => loadHistory());
  const [shop, setShop] = useState(() => loadShopInput());
  const [showOnboarding, setShowOnboarding] = useState(() => !onboardingComplete());
  const [health, setHealth] = useState<{ openrouterConfigured: boolean; primaryModel: string } | undefined>();

  useEffect(() => {
    getHealth()
      .then((value) => setHealth(value))
      .catch(() => setHealth(undefined));
  }, []);

  const generatedCount = history.length;

  return (
    <>
      <section className="grid gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-lg border border-moss/10 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.18em] text-coral">Dashboard</p>
              <h1 className="mt-3 text-4xl font-black text-ink sm:text-5xl">Welcome back.</h1>
              <p className="mt-3 max-w-2xl text-ink/68">Start with a free Etsy generator, link a shop note locally, or reopen a recent draft.</p>
            </div>
            <Link
              href="/document-create?type=19"
              navigate={navigate}
              className="inline-flex items-center justify-center gap-2 rounded-md bg-moss px-4 py-3 font-bold text-white hover:bg-ink"
            >
              <PenLine className="h-4 w-4" />
              New description
            </Link>
          </div>
        </div>

        <form
          className="rounded-lg border border-moss/10 bg-white p-6 shadow-sm"
          onSubmit={(event) => {
            event.preventDefault();
            saveShopInput(shop);
          }}
        >
          <label htmlFor="shop" className="text-sm font-bold text-ink">
            Shop name or URL
          </label>
          <div className="mt-3 flex flex-col gap-3 sm:flex-row">
            <input
              id="shop"
              value={shop}
              onChange={(event) => setShop(event.target.value)}
              className="min-h-11 flex-1 rounded-md border border-moss/15 bg-cloud px-3 text-sm outline-none"
              placeholder="mystore.etsy.com"
            />
            <button type="submit" className="rounded-md bg-coral px-4 py-2.5 text-sm font-bold text-white hover:bg-[#bf563b]">
              Save
            </button>
          </div>
          <p className="mt-3 text-xs leading-5 text-ink/52">Stored only in this browser for quick context.</p>
        </form>
      </section>

      <section className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        {[
          ["Plan", "Free", "No subscription gate", Sparkles],
          ["Generated", String(generatedCount), "Recent local drafts", FileText],
          ["AI status", health?.openrouterConfigured ? "Configured" : "Needs key", health?.primaryModel ?? "openrouter/free", Settings],
          ["Tools", String(TOOLS.length), "Core AI generators", LayoutDashboard]
        ].map(([label, value, caption, Icon]) => (
          <div key={label as string} className="rounded-lg border border-moss/10 bg-white p-5 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-ink/56">{label as string}</p>
                <p className="mt-2 text-3xl font-black text-ink">{value as string}</p>
                <p className="mt-1 text-xs text-ink/52">{caption as string}</p>
              </div>
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-mint/12 text-moss">
                <Icon className="h-5 w-5" />
              </span>
            </div>
          </div>
        ))}
      </section>

      <section className="mt-4 grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="rounded-lg border border-moss/10 bg-white shadow-sm">
          <div className="border-b border-moss/10 px-5 py-4">
            <p className="text-sm text-ink/58">Shortcuts</p>
            <h2 className="text-xl font-black">Your Most Used Tools</h2>
          </div>
          <div className="grid gap-2 p-4 sm:grid-cols-2">
            {TOOLS.slice(0, 4).map((tool) => {
              const Icon = toolIcons[tool.id];
              return (
                <Link
                  key={tool.id}
                  href={`/tools/${tool.slug}`}
                  navigate={navigate}
                  className="flex items-center gap-3 rounded-md border border-moss/10 bg-paper p-3 hover:border-mint/50"
                >
                  <Icon className="h-5 w-5 text-mint" />
                  <div>
                    <p className="text-sm font-bold">{tool.shortTitle}</p>
                    <p className="text-xs text-ink/52">{tool.eyebrow}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="rounded-lg border border-moss/10 bg-white shadow-sm">
          <div className="border-b border-moss/10 px-5 py-4">
            <p className="text-sm text-ink/58">History</p>
            <h2 className="text-xl font-black">Recent generations</h2>
          </div>
          <div className="max-h-80 space-y-3 overflow-y-auto p-4 scrollbar-soft">
            {history.length ? (
              history.slice(0, 5).map((item) => (
                <Link
                  key={item.id}
                  href={`/tools/${TOOLS.find((tool) => tool.id === item.tool)?.slug ?? defaultTool().slug}`}
                  navigate={navigate}
                  className="block rounded-md border border-moss/10 bg-paper p-4 hover:border-mint/50"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold">{item.productName}</p>
                      <p className="mt-1 text-sm text-ink/58">{item.toolTitle}</p>
                    </div>
                    <span className="text-xs text-ink/42">{new Date(item.createdAt).toLocaleDateString()}</span>
                  </div>
                </Link>
              ))
            ) : (
              <div className="rounded-md border border-dashed border-moss/20 bg-paper p-8 text-center">
                <FileText className="mx-auto h-8 w-8 text-mint" />
                <p className="mt-3 font-bold">No generated drafts yet.</p>
                <p className="mt-1 text-sm text-ink/56">Start with a description, title, or tag generator.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="mt-4 rounded-lg border border-moss/10 bg-white shadow-sm">
        <div className="border-b border-moss/10 px-5 py-4">
          <p className="text-sm text-ink/58">Unavailable data tools</p>
          <h2 className="text-xl font-black">Visible, but not faked</h2>
        </div>
        <div className="grid gap-3 p-4 md:grid-cols-2 xl:grid-cols-3">
          {RESEARCH_TOOLS.map((tool) => {
            const Icon = researchIcons[tool.slug] ?? BookOpen;
            return (
              <Link
                key={tool.slug}
                href={`/tools/${tool.slug}`}
                navigate={navigate}
                className="rounded-md border border-gold/20 bg-[#fff8ec] p-4 hover:border-gold/60"
              >
                <div className="flex items-center gap-3">
                  <Icon className="h-5 w-5 text-gold" />
                  <h3 className="font-bold">{tool.title}</h3>
                </div>
                <p className="mt-2 text-sm leading-6 text-ink/62">{tool.description}</p>
              </Link>
            );
          })}
        </div>
      </section>

      {showOnboarding ? (
        <OnboardingModal
          onDone={(answers) => {
            saveOnboarding(answers);
            setShowOnboarding(false);
            setHistory(loadHistory());
          }}
        />
      ) : null}
    </>
  );
}

function OnboardingModal({ onDone }: { onDone: (answers: OnboardingAnswers) => void }) {
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<OnboardingAnswers>({});
  const step = onboardingSteps[stepIndex];
  const selected = answers[step.key];
  const progress = ((stepIndex + 1) / onboardingSteps.length) * 100;

  const finish = (skipped = false) => {
    onDone({ ...answers, skipped });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/45 px-4 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl border border-moss/10 bg-white shadow-soft">
        <div className="px-6 pt-6">
          <div className="mb-2 text-center text-xs font-bold text-ink/50">
            {stepIndex + 1} of {onboardingSteps.length}
          </div>
          <div className="h-1 overflow-hidden rounded-full bg-cloud">
            <div className="h-full rounded-full bg-mint transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
        <div className="px-6 pb-6 pt-5 text-center">
          <h2 className="text-xl font-black">Quick Questions</h2>
          <p className="mt-2 text-sm text-ink/58">Personalize the default prompts in this browser.</p>
        </div>
        <div className="px-6 pb-6">
          <p className="mb-4 text-sm font-bold">{step.prompt}</p>
          <div className="grid grid-cols-2 gap-2">
            {step.options.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={classNames(
                  "min-h-12 rounded-lg border px-3 py-2 text-sm font-semibold transition",
                  selected === value ? "border-mint bg-mint/14 text-moss" : "border-moss/10 bg-cloud text-ink/72 hover:border-mint/50"
                )}
                onClick={() => setAnswers((current) => ({ ...current, [step.key]: value }))}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="mt-8 flex gap-3">
            <button
              type="button"
              disabled={!selected}
              className="min-h-11 flex-1 rounded-xl bg-moss px-4 font-bold text-white disabled:cursor-not-allowed disabled:opacity-45"
              onClick={() => {
                if (stepIndex === onboardingSteps.length - 1) {
                  finish(false);
                } else {
                  setStepIndex((index) => index + 1);
                }
              }}
            >
              {stepIndex === onboardingSteps.length - 1 ? "Done" : "Continue"}
            </button>
            <button type="button" className="min-h-11 rounded-xl px-4 font-bold text-ink/58 hover:bg-cloud" onClick={() => finish(true)}>
              Skip
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToolPage({ tool }: { tool: ToolDefinition }) {
  const [inputs, setInputs] = useState<Record<string, string | number>>(() => ({ ...tool.starterInputs }));
  const [result, setResult] = useState<GenerateSuccess | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);

  useEffect(() => {
    setInputs({ ...tool.starterInputs });
    setResult(undefined);
    setError(undefined);
  }, [tool]);

  const submit = async () => {
    setLoading(true);
    setError(undefined);

    try {
      const response = await generateContent({
        tool: tool.id,
        inputs,
        onboarding: loadOnboardingData(),
        locale: "en"
      });

      if (!response.ok) {
        setError(response.message);
        return;
      }

      setResult(response);
      const productName = String(inputs.productName || inputs.niche || tool.title);
      addHistoryItem({
        id: makeHistoryId(),
        tool: tool.id,
        toolTitle: tool.title,
        productName,
        createdAt: new Date().toISOString(),
        output: response.output,
        model: response.model
      });
      setHistoryVersion((version) => version + 1);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Generation failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
      <section className="rounded-lg border border-moss/10 bg-white shadow-sm">
        <div className="border-b border-moss/10 px-5 py-5">
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-coral">{tool.eyebrow}</p>
          <h1 className="mt-2 text-3xl font-black text-ink sm:text-4xl">{tool.title}</h1>
          <p className="mt-3 leading-7 text-ink/66">{tool.description}</p>
        </div>

        <form
          className="space-y-4 p-5"
          onSubmit={(event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            void submit();
          }}
        >
          {tool.fields.map((field) => (
            <ToolFieldControl
              key={field.name}
              field={field}
              value={inputs[field.name] ?? ""}
              onChange={(value) => setInputs((current) => ({ ...current, [field.name]: value }))}
            />
          ))}

          {error ? (
            <div className="rounded-md border border-coral/25 bg-coral/10 px-4 py-3 text-sm font-semibold text-[#9c3e2a]" role="alert">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex min-h-12 flex-1 items-center justify-center gap-2 rounded-md bg-moss px-5 font-bold text-white hover:bg-ink disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {loading ? "Generating" : tool.actionLabel}
            </button>
            <button
              type="button"
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md border border-moss/15 px-5 font-bold text-moss hover:bg-cloud"
              onClick={() => setInputs({ ...tool.starterInputs })}
            >
              Reset sample
            </button>
          </div>
        </form>
      </section>

      <section className="space-y-4">
        <OutputPanel result={result} tool={tool} onRegenerate={() => void submit()} loading={loading} />
        <RecentHistory tool={tool} version={historyVersion} />
      </section>
    </div>
  );
}

function ToolFieldControl({
  field,
  value,
  onChange
}: {
  field: ToolField;
  value: string | number;
  onChange: (value: string | number) => void;
}) {
  const id = `field-${field.name}`;
  const baseClass = "mt-2 w-full rounded-md border border-moss/15 bg-cloud px-3 py-3 text-sm text-ink outline-none placeholder:text-ink/34";

  return (
    <div>
      <label htmlFor={id} className="text-sm font-bold text-ink">
        {field.label}
        {field.required ? <span className="text-coral"> *</span> : null}
      </label>
      {field.type === "textarea" ? (
        <textarea
          id={id}
          rows={4}
          className={baseClass}
          placeholder={field.placeholder}
          value={String(value)}
          required={field.required}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : field.type === "select" ? (
        <select id={id} className={baseClass} value={String(value)} onChange={(event) => onChange(event.target.value)}>
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={field.type === "number" ? "number" : "text"}
          className={baseClass}
          placeholder={field.placeholder}
          value={value}
          required={field.required}
          onChange={(event) => onChange(field.type === "number" ? Number(event.target.value) : event.target.value)}
        />
      )}
    </div>
  );
}

function OutputPanel({
  result,
  tool,
  loading,
  onRegenerate
}: {
  result: GenerateSuccess | undefined;
  tool: ToolDefinition;
  loading: boolean;
  onRegenerate: () => void;
}) {
  return (
    <div className="rounded-lg border border-moss/10 bg-white shadow-sm">
      <div className="flex items-center justify-between gap-3 border-b border-moss/10 px-5 py-4">
        <div>
          <p className="text-sm text-ink/58">Output</p>
          <h2 className="text-xl font-black">Generated listing draft</h2>
        </div>
        <button
          type="button"
          disabled={!result || loading}
          className="inline-flex items-center gap-2 rounded-md border border-moss/15 px-3 py-2 text-sm font-bold text-moss hover:bg-cloud disabled:cursor-not-allowed disabled:opacity-45"
          onClick={onRegenerate}
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Regenerate
        </button>
      </div>

      {result ? (
        <div className="space-y-4 p-5">
          <div className="rounded-md bg-cloud px-3 py-2 text-xs font-semibold text-ink/56">Model: {result.model}</div>
          {tool.outputKeys.map((key) => (
            <OutputBlock key={key} label={outputLabel(key)} value={result.output[key]} />
          ))}
        </div>
      ) : (
        <div className="p-5">
          <div className="rounded-md border border-dashed border-moss/20 bg-paper p-8 text-center">
            <Clipboard className="mx-auto h-9 w-9 text-mint" />
            <p className="mt-3 font-bold">Your generated output will appear here.</p>
            <p className="mt-1 text-sm text-ink/56">The API returns structured JSON for this tool.</p>
          </div>
        </div>
      )}
    </div>
  );
}

function OutputBlock({ label, value }: { label: string; value: string | string[] | undefined }) {
  const text = Array.isArray(value) ? value.join(", ") : value ?? "";

  return (
    <div className="rounded-md border border-moss/10 bg-paper p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="font-black">{label}</h3>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-bold text-moss hover:bg-cloud"
          onClick={() => void navigator.clipboard.writeText(text)}
        >
          <Copy className="h-3.5 w-3.5" />
          Copy
        </button>
      </div>
      {Array.isArray(value) ? (
        <div className="flex flex-wrap gap-2">
          {value.map((item) => (
            <span key={item} className="rounded-full border border-mint/25 bg-mint/10 px-3 py-1 text-sm font-semibold text-moss">
              {item}
            </span>
          ))}
        </div>
      ) : (
        <p className="whitespace-pre-line leading-7 text-ink/76">{value}</p>
      )}
    </div>
  );
}

function outputLabel(key: OutputKey): string {
  const labels: Record<OutputKey, string> = {
    title: "Title",
    description: "Description",
    tags: "Tags",
    keywords: "Keywords",
    ideas: "Ideas",
    plan: "Plan"
  };

  return labels[key];
}

function RecentHistory({ tool, version }: { tool: ToolDefinition; version: number }) {
  const [history, setHistory] = useState<GenerationHistoryItem[]>(() => loadHistory());

  useEffect(() => {
    setHistory(loadHistory());
  }, [version]);

  const items = history.filter((item) => item.tool === tool.id).slice(0, 4);

  return (
    <div className="rounded-lg border border-moss/10 bg-white shadow-sm">
      <div className="border-b border-moss/10 px-5 py-4">
        <p className="text-sm text-ink/58">Local history</p>
        <h2 className="text-xl font-black">Recent {tool.shortTitle.toLowerCase()} drafts</h2>
      </div>
      <div className="space-y-3 p-5">
        {items.length ? (
          items.map((item) => (
            <div key={item.id} className="rounded-md border border-moss/10 bg-paper p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-bold">{item.productName}</p>
                  <p className="mt-1 text-xs text-ink/52">{item.model}</p>
                </div>
                <span className="text-xs text-ink/42">{new Date(item.createdAt).toLocaleTimeString()}</span>
              </div>
            </div>
          ))
        ) : (
          <p className="rounded-md border border-dashed border-moss/20 bg-paper p-6 text-center text-sm text-ink/58">
            No local drafts for this tool yet.
          </p>
        )}
      </div>
    </div>
  );
}

function ResearchUnavailable({ tool, navigate }: { tool: ResearchTool; navigate: (href: string) => void }) {
  const Icon = researchIcons[tool.slug] ?? BookOpen;

  return (
    <section className="rounded-lg border border-gold/25 bg-[#fff8ec] p-7 shadow-sm">
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 items-center justify-center rounded-md bg-gold/15 text-gold">
          <Icon className="h-6 w-6" />
        </span>
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.18em] text-gold">{tool.section}</p>
          <h1 className="mt-2 text-4xl font-black text-ink">{tool.title}</h1>
          <p className="mt-4 max-w-3xl leading-7 text-ink/68">{tool.description}</p>
          <p className="mt-3 max-w-3xl leading-7 text-ink/68">
            This restored build keeps the navigation visible while avoiding fabricated marketplace metrics.
          </p>
          <Link
            href="/tools/keyword-generator"
            navigate={navigate}
            className="mt-7 inline-flex items-center gap-2 rounded-md bg-moss px-5 py-3 font-bold text-white hover:bg-ink"
          >
            Use keyword generator <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default App;
